import { tr as __tr, useLocale as __useLocale } from "../i18n/runtime";
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, CreditCard, User, Settings, LogOut, Plane, Hotel, Bus, FileText, Clock, Check, X, ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import OperationsPage from './admin/OperationsPage';
const API = '/api';
const CustomerDashboard = () => {
  __useLocale();
  const {
    user,
    token,
    logout
  } = useAuth();
  const {
    t,
    language,
    toggleLanguage
  } = useLanguage();
  const [activeTab, setActiveTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const headers = {
    Authorization: `Bearer ${token}`
  };
  useEffect(() => {
    fetchData();
  }, [activeTab]);
  const fetchData = async () => {
    setLoading(true);
    setLoadError('');
    try {
      if (activeTab === 'bookings') {
        const res = await axios.get(`${API}/records.php?resource=bookings`, {
          headers
        });
        setBookings(res.data);
      } else if (activeTab === 'payments') {
        const res = await axios.get(`${API}/records.php?resource=payments`, {
          headers
        });
        setTransactions(res.data);
      }
    } catch (error) {
      setLoadError(__tr("تعذر تحميل البيانات. يرجى إعادة المحاولة."));
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  const getBookingIcon = type => {
    const icons = {
      hotel: Hotel,
      bus: Bus,
      visa: FileText,
      flight: Plane
    };
    return icons[type] || Calendar;
  };
  const getStatusColor = status => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      confirmed: 'bg-blue-100 text-blue-700 border-blue-200',
      completed: 'bg-green-100 text-green-700 border-green-200',
      cancelled: 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };
  return <div className="min-h-screen bg-gray-50" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-2xl font-bold text-[#6B5B95]">{__tr(t('brand'))}</span>
            <Plane className="w-6 h-6 text-[#FF8C42]" />
          </Link>
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={toggleLanguage}>
              {__tr(language === 'ar' ? 'EN' : "عربي")}
            </Button>
            <Button variant="ghost" onClick={logout} className="text-red-600">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Welcome Card */}
        <div className="bg-gradient-to-r from-[#6B5B95] to-[#5B4B85] rounded-2xl p-6 mb-8 text-white">
          <h1 className="text-2xl font-bold mb-2">
            {__tr(t('dashboard.welcome'))}{__tr(", ")}{__tr(user?.full_name)}
          </h1>
          <p className="text-white/80">{__tr(t('nav.myBookings'))}</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button variant={activeTab === 'bookings' ? 'default' : 'outline'} onClick={() => setActiveTab('bookings')} className={activeTab === 'bookings' ? 'bg-[#6B5B95]' : ''}>
            <Calendar className="w-4 h-4 ml-2" />
            {__tr(t('dashboard.bookings'))}
          </Button>
          <Button variant={activeTab === 'payments' ? 'default' : 'outline'} onClick={() => setActiveTab('payments')} className={activeTab === 'payments' ? 'bg-[#6B5B95]' : ''}>
            <CreditCard className="w-4 h-4 ml-2" />
            {__tr(t('dashboard.payments'))}
          </Button>
          <Button variant={activeTab === 'profile' ? 'default' : 'outline'} onClick={() => setActiveTab('profile')} className={activeTab === 'profile' ? 'bg-[#6B5B95]' : ''}>
            <User className="w-4 h-4 ml-2" />
            {__tr(t('nav.profile'))}
          </Button>
        </div>

        {/* Content */}
        {__tr(activeTab === 'bookings' && <OperationsPage customerView />)}
        {__tr(loadError && <p role="alert" className="text-red-700">{__tr(loadError)} <button onClick={fetchData}>{__tr("إعادة المحاولة")}</button></p>)}
        {__tr(loading ? <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6B5B95]"></div>
          </div> : <>
            {/* Payments */}
            {__tr(activeTab === 'payments' && <div className="bg-white rounded-xl shadow-sm">
                {__tr(transactions.length === 0 ? <div className="p-12 text-center">
                    <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">{__tr(t('common.noData'))}</p>
                  </div> : <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-right py-4 px-6 text-gray-600">{__tr("ID")}</th>
                          <th className="text-right py-4 px-6 text-gray-600">{__tr(t('dashboard.totalRevenue'))}</th>
                          <th className="text-right py-4 px-6 text-gray-600">{__tr(t('status.pending'))}</th>
                          <th className="text-right py-4 px-6 text-gray-600">{__tr(t('booking.departure'))}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {__tr(transactions.map(tx => <tr key={tx.id} className="border-b hover:bg-gray-50">
                            <td className="py-4 px-6 font-mono text-sm">{__tr(tx.id?.slice(0, 8))}</td>
                            <td className="py-4 px-6 font-bold">${__tr(tx.amount)}</td>
                            <td className="py-4 px-6">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(tx.status)}`}>
                                {__tr(t(`status.${tx.status}`))}
                              </span>
                            </td>
                            <td className="py-4 px-6 text-sm text-gray-500">
                              {__tr(new Date(tx.created_at).toLocaleDateString())}
                            </td>
                          </tr>))}
                      </tbody>
                    </table>
                  </div>)}
              </div>)}

            {/* Profile */}
            {__tr(activeTab === 'profile' && <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-6">{__tr(t('nav.profile'))}</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="w-16 h-16 bg-[#6B5B95] rounded-full flex items-center justify-center">
                      <User className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-800">{__tr(user?.full_name)}</p>
                      <p className="text-sm text-gray-500">{__tr(user?.email)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-sm text-gray-500">{__tr(t('auth.phone'))}</p>
                      <p className="font-medium">{__tr(user?.phone || '-')}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-sm text-gray-500">{__tr(t('dashboard.users'))}</p>
                      <p className="font-medium capitalize">{__tr(user?.role)}</p>
                    </div>
                  </div>
                </div>
              </div>)}
          </>)}
      </div>
    </div>;
};
export default CustomerDashboard;
